import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import "react-toastify/dist/ReactToastify.css";
import { useAuth } from "Pages/PrivateRoute/AuthContext";
import { useToast } from "../../hooks/use-toast";
import { LoginAxiosInstance } from "../../Network/LoginAxiosInstance";

interface LoginPageProps {}

/**
 * QA Automation IDs
 * Keep these values stable. Automation scripts should depend on these IDs,
 * not on text, CSS classes, or element order.
 */
const QA_IDS = {
    page: "page-login",
    bgGradient1: "decor-login-bg-gradient-1",
    bgGradient2: "decor-login-bg-gradient-2",
    bgGradient3: "decor-login-bg-gradient-3",
    bgPattern: "decor-login-bg-pattern",
    decorCircle1: "decor-login-circle-1",
    decorCircle2: "decor-login-circle-2",

    leftSide: "section-login-branding",
    brandingContent: "container-login-branding-content",
    logoContainer: "container-login-logo",
    logoIcon: "icon-login-logo",
    logoSvg: "svg-login-logo",
    brandName: "text-login-brand-name",
    brandTitle: "text-login-brand-title",
    brandSubtitle: "text-login-brand-subtitle",
    featuresList: "list-login-features",
    featureItem: "item-login-feature",
    featureIcon: "icon-login-feature",
    featureText: "text-login-feature",

    rightSide: "section-login-form",
    loginCard: "card-login",
    header: "header-login",
    title: "title-login-sign-in",
    subtitle: "text-login-subtitle",
    form: "form-login",
    formContainer: "container-login-form",

    companyWrapper: "wrapper-company-name",
    companyLabel: "label-company-name",
    companyInputContainer: "container-company-name-input",
    companyIcon: "icon-company-name",
    companyInput: "input-company-name",
    companyError: "error-company-name",
    companyLoginError: "CompanyName-error-message",

    usernameWrapper: "wrapper-username",
    usernameLabel: "label-username",
    usernameInputContainer: "container-username-input",
    usernameIcon: "icon-username",
    usernameInput: "input-username",
    usernameError: "error-username",
    usernameLoginError: "UserName-error-message",

    passwordWrapper: "wrapper-password",
    passwordLabel: "label-password",
    passwordInputContainer: "container-password-input",
    passwordIcon: "icon-password",
    passwordInput: "input-password",
    passwordToggle: "btn-toggle-password-visibility",
    passwordToggleShowIcon: "icon-password-show",
    passwordToggleHideIcon: "icon-password-hide",
    passwordError: "error-password",
    loginError: "login-error-message",
    deactivatedError: "deactivated-error-message",

    submitButton: "btn-login-submit",
    submitButtonText: "text-login-submit",
    submitButtonIcon: "icon-login-submit",
    submitLoader: "loader-login-submit",
};

const GENERIC_LOGIN_ERROR = "Invalid login credentials.";

const Login: React.FC<LoginPageProps> = () => {
    const [name, setCompanyName] = useState("");
    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);

    const [nameError, setNameError] = useState("");
    const [usernameError, setUsernameError] = useState("");
    const [passwordError, setPasswordError] = useState("");
    const [loginError, setLoginError] = useState("");
    const [deactivatedError, setDeactivatedError] = useState("");

    const navigate = useNavigate();
    const { login, isAuthenticated } = useAuth();
    const { toast } = useToast();

    React.useEffect(() => {
        if (isAuthenticated) {
            navigate("/dashboard", { replace: true });
        }
    }, [isAuthenticated, navigate]);

    React.useEffect(() => {
        const existingStyle = document.head.querySelector("[data-lp-style]");
        if (existingStyle) return;

        const styleSheet = document.createElement("style");
        styleSheet.setAttribute("data-lp-style", "1");
        styleSheet.textContent = `
.login-page input:focus { border-color: #3b82f6 !important; box-shadow: 0 0 0 4px rgba(59,130,246,.15), 0 4px 12px rgba(0,0,0,.1) !important; }
.login-page button:hover:not(:disabled) { background: linear-gradient(135deg,#1e40af 0%,#2563eb 50%,#7c3aed 100%) !important; transform: translateY(-2px); box-shadow: 0 12px 32px rgba(59,130,246,.5) !important; }
.login-page button:hover:not(:disabled) svg { transform: translateX(4px); }
.login-page button:active:not(:disabled) { transform: translateY(0px); }
.login-page input[type="password"] { padding-right: 48px !important; }
@keyframes slideUp { from { opacity:0; transform:translateY(30px) scale(.95); } to { opacity:1; transform:translateY(0) scale(1); } }
@keyframes float   { 0%,100% { transform:translateY(0); } 50% { transform:translateY(-20px); } }
@keyframes spin    { to { transform:rotate(360deg); } }
@keyframes pulse   { 0%,100% { opacity:.4; transform:scale(1); } 50% { opacity:.6; transform:scale(1.05); } }
`;
        document.head.appendChild(styleSheet);
    }, []);

    const validateCompanyName = (value: string) => {
        if (!value.trim()) {
            setNameError("Company name is required");
            return false;
        }
        if (value.trim().length < 3) {
            setNameError("Company name must be at least 3 characters");
            return false;
        }
        setNameError("");
        return true;
    };

    const validateUsername = (value: string) => {
        if (!value.trim()) {
            setUsernameError("Username is required");
            return false;
        }
        if (value.trim().length < 3) {
            setUsernameError("Username must be at least 3 characters");
            return false;
        }
        setUsernameError("");
        return true;
    };

    const validatePassword = (value: string) => {
        if (!value) {
            setPasswordError("Password is required");
            return false;
        }
        if (value.length < 6) {
            setPasswordError("Password must be at least 6 characters");
            return false;
        }
        setPasswordError("");
        return true;
    };

    const handleLogin = async () => {
        const isNameValid = validateCompanyName(name);
        const isUsernameValid = validateUsername(username);
        const isPasswordValid = validatePassword(password);

        if (!isNameValid || !isUsernameValid || !isPasswordValid) return;

        setLoginError("");
        setDeactivatedError("");
        setIsLoading(true);

        try {
            const response = await LoginAxiosInstance.put("/login", {
                name,
                username,
                password,
            });

            const result = response.data;

            if (result.success) {
                const accessToken = result.accessToken || result.token;
                const refreshToken = result.refreshToken || null;

                if (!accessToken) throw new Error("Missing access token");

                const userArr = Array.isArray(result.user) ? result.user : [result.user];
                const userData = userArr[0] ?? {};

                const userToStore = {
                    id: userData.id,
                    name: userData.name,
                    username: userData.username,
                    role: userData.role,
                    companyid: userData.companyid,
                    active: userData.active,
                };

                login(accessToken, refreshToken, userToStore, name);

                try {
                    const existingEvents = JSON.parse(localStorage.getItem("loginEvents") || "[]");
                    const now = new Date();

                    existingEvents.unshift({
                        userId: userData.id,
                        name: userData.name,
                        username: userData.username,
                        role: userData.role,
                        companyName: name,
                        loginAt: now.toISOString(),
                        loginDate: `${String(now.getDate()).padStart(2, "0")}/${String(now.getMonth() + 1).padStart(2, "0")}/${String(now.getFullYear()).slice(-2)}`,
                        loginTime: `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`,
                    });

                    if (existingEvents.length > 100) existingEvents.splice(100);
                    localStorage.setItem("loginEvents", JSON.stringify(existingEvents));
                } catch {
                    // Login tracking is non-critical.
                }

                toast({
                    title: "Success",
                    description: "User Login Successfully.",
                });

                setTimeout(() => navigate("/dashboard"), 1000);
            } else {
                const backendError = result.error || "";
                const isDeactivated = /deactivat|inactive|disabled|suspended/i.test(backendError);
                const displayError = isDeactivated ? backendError : GENERIC_LOGIN_ERROR;

                applyLoginError(displayError);
                toast({
                    title: "Error",
                    description: displayError,
                    variant: "destructive",
                });
            }
        } catch (error: any) {
            const backendError = error?.response?.data?.error || "";
            const isDeactivated = /deactivat|inactive|disabled|suspended/i.test(backendError);
            const displayError = isDeactivated ? backendError : GENERIC_LOGIN_ERROR;

            applyLoginError(displayError);
            toast({
                title: "Error",
                description: displayError,
                variant: "destructive",
            });
        } finally {
            setIsLoading(false);
        }
    };

    // Routes a backend login error to the right UI slot:
    // deactivated accounts get their own warning testid, everything
    // else (wrong credentials, user not found, etc.) uses the generic one.
    const applyLoginError = (errorMessage: string) => {
        const isDeactivated = /deactivat|inactive|disabled|suspended/i.test(errorMessage);
        if (isDeactivated) {
            setDeactivatedError(errorMessage);
            setLoginError("");
        } else {
            setLoginError(errorMessage);
            setDeactivatedError("");
        }
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        await handleLogin();
    };

    return (
        <div
            id={QA_IDS.page}
            data-testid={QA_IDS.page}
            data-qa={QA_IDS.page}
            className="login-page"
            style={styles.fullContainer}
        >
            <div data-testid={QA_IDS.bgGradient1} data-qa={QA_IDS.bgGradient1} style={styles.bgGradient1}></div>
            <div data-testid={QA_IDS.bgGradient2} data-qa={QA_IDS.bgGradient2} style={styles.bgGradient2}></div>
            <div data-testid={QA_IDS.bgGradient3} data-qa={QA_IDS.bgGradient3} style={styles.bgGradient3}></div>
            <div data-testid={QA_IDS.bgPattern} data-qa={QA_IDS.bgPattern} style={styles.bgPattern}></div>
            <div data-testid={QA_IDS.decorCircle1} data-qa={QA_IDS.decorCircle1} style={styles.decorCircle1}></div>
            <div data-testid={QA_IDS.decorCircle2} data-qa={QA_IDS.decorCircle2} style={styles.decorCircle2}></div>

            {/* Left Side - Branding */}
            <section data-testid={QA_IDS.leftSide} data-qa={QA_IDS.leftSide} style={styles.leftSide}>
                <div data-testid={QA_IDS.brandingContent} data-qa={QA_IDS.brandingContent} style={styles.brandingContent}>
                    <div data-testid={QA_IDS.logoContainer} data-qa={QA_IDS.logoContainer} style={styles.logoContainer}>
                        <div data-testid={QA_IDS.logoIcon} data-qa={QA_IDS.logoIcon} style={styles.logoIcon}>
                            <svg
                                data-testid={QA_IDS.logoSvg}
                                data-qa={QA_IDS.logoSvg}
                                width="48"
                                height="48"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="white"
                                strokeWidth="2"
                                aria-hidden="true"
                            >
                                <path d="M12 2L2 7l10 5 10-5-10-5z" />
                                <path d="M2 17l10 5 10-5" />
                                <path d="M2 12l10 5 10-5" />
                            </svg>
                        </div>
                        <h1 data-testid={QA_IDS.brandName} data-qa={QA_IDS.brandName} style={styles.brandName}>
                            LEEN
                        </h1>
                    </div>

                    <h2 data-testid={QA_IDS.brandTitle} data-qa={QA_IDS.brandTitle} style={styles.brandTitle}>
                        Precision in Every Claim
                    </h2>

                    <p data-testid={QA_IDS.brandSubtitle} data-qa={QA_IDS.brandSubtitle} style={styles.brandSubtitle}>
                        Perfection in Adjudication. Empower your team with LEEN — the comprehensive modular system
                        designed to streamline medical claim reviews, eliminate human error, and enforce 100% compliance with Gulf market regulations.
                    </p>

                    <div data-testid={QA_IDS.featuresList} data-qa={QA_IDS.featuresList} style={styles.features}>
                        {["Smart Automation", "Modular Design", "Advanced Insights", "Market Regulations", "Robust Scrubbing"].map((feature, index) => (
                            <div
                                key={feature}
                                data-testid={`${QA_IDS.featureItem}-${index + 1}`}
                                data-qa={`${QA_IDS.featureItem}-${index + 1}`}
                                style={styles.feature}
                            >
                                <div
                                    data-testid={`${QA_IDS.featureIcon}-${index + 1}`}
                                    data-qa={`${QA_IDS.featureIcon}-${index + 1}`}
                                    style={styles.featureIcon}
                                    aria-hidden="true"
                                >
                                    ✓
                                </div>
                                <span
                                    data-testid={`${QA_IDS.featureText}-${index + 1}`}
                                    data-qa={`${QA_IDS.featureText}-${index + 1}`}
                                >
                                    {feature}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Right Side - Login Form */}
            <section data-testid={QA_IDS.rightSide} data-qa={QA_IDS.rightSide} style={styles.rightSide}>
                <div data-testid={QA_IDS.loginCard} data-qa={QA_IDS.loginCard} style={styles.loginCard}>
                    <div data-testid={QA_IDS.header} data-qa={QA_IDS.header} style={styles.header}>
                        <h2 data-testid={QA_IDS.title} data-qa={QA_IDS.title} style={styles.title}>
                            Sign In
                        </h2>
                        <p data-testid={QA_IDS.subtitle} data-qa={QA_IDS.subtitle} style={styles.subtitle}>
                            Enter your credentials to access your account
                        </p>
                    </div>

                    <form
                        id={QA_IDS.form}
                        data-testid={QA_IDS.form}
                        data-qa={QA_IDS.form}
                        style={styles.formContainer}
                        onSubmit={handleSubmit}
                        noValidate
                    >
                        {/* Company Name */}
                        <div data-testid={QA_IDS.companyWrapper} data-qa={QA_IDS.companyWrapper} style={styles.inputWrapper}>
                            <label
                                id={QA_IDS.companyLabel}
                                data-testid={QA_IDS.companyLabel}
                                data-qa={QA_IDS.companyLabel}
                                htmlFor={QA_IDS.companyInput}
                                style={styles.label}
                            >
                                Company Name
                            </label>

                            <div
                                data-testid={QA_IDS.companyInputContainer}
                                data-qa={QA_IDS.companyInputContainer}
                                style={styles.inputContainer}
                            >
                                <svg
                                    data-testid={QA_IDS.companyIcon}
                                    data-qa={QA_IDS.companyIcon}
                                    style={styles.inputIcon}
                                    width="18"
                                    height="18"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    aria-hidden="true"
                                >
                                    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                                    <polyline points="9 22 9 12 15 12 15 22" />
                                </svg>

                                <input
                                    id={QA_IDS.companyInput}
                                    data-testid={QA_IDS.companyInput}
                                    data-qa={QA_IDS.companyInput}
                                    style={{ ...styles.input, ...(nameError ? styles.inputError : {}) }}
                                    type="text"
                                    placeholder="Enter your company name"
                                    value={name}
                                    aria-invalid={Boolean(nameError)}
                                    aria-describedby={nameError ? QA_IDS.companyError : undefined}
                                    autoComplete="organization"
                                    onChange={(e) => {
                                        setCompanyName(e.target.value);
                                        validateCompanyName(e.target.value);
                                        if (loginError) setLoginError("");
                                        if (deactivatedError) setDeactivatedError("");
                                    }}
                                />
                            </div>

                            {nameError && (
                                <span
                                    id={QA_IDS.companyError}
                                    data-testid={QA_IDS.companyLoginError}
                                    data-qa={QA_IDS.companyLoginError}
                                    style={styles.errorText}
                                    role="alert"
                                >
                                    {nameError}
                                </span>
                            )}
                        </div>

                        {/* Username */}
                        <div data-testid={QA_IDS.usernameWrapper} data-qa={QA_IDS.usernameWrapper} style={styles.inputWrapper}>
                            <label
                                id={QA_IDS.usernameLabel}
                                data-testid={QA_IDS.usernameLabel}
                                data-qa={QA_IDS.usernameLabel}
                                htmlFor={QA_IDS.usernameInput}
                                style={styles.label}
                            >
                                Username
                            </label>

                            <div
                                data-testid={QA_IDS.usernameInputContainer}
                                data-qa={QA_IDS.usernameInputContainer}
                                style={styles.inputContainer}
                            >
                                <svg
                                    data-testid={QA_IDS.usernameIcon}
                                    data-qa={QA_IDS.usernameIcon}
                                    style={styles.inputIcon}
                                    width="18"
                                    height="18"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    aria-hidden="true"
                                >
                                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                                    <circle cx="12" cy="7" r="4" />
                                </svg>

                                <input
                                    id={QA_IDS.usernameInput}
                                    data-testid={QA_IDS.usernameInput}
                                    data-qa={QA_IDS.usernameInput}
                                    style={{ ...styles.input, ...(usernameError ? styles.inputError : {}) }}
                                    type="text"
                                    placeholder="Enter your username"
                                    value={username}
                                    aria-invalid={Boolean(usernameError)}
                                    aria-describedby={usernameError ? QA_IDS.usernameError : undefined}
                                    autoComplete="username"
                                    onChange={(e) => {
                                        setUsername(e.target.value);
                                        validateUsername(e.target.value);
                                        if (loginError) setLoginError("");
                                        if (deactivatedError) setDeactivatedError("");
                                    }}
                                />
                            </div>

                            {usernameError && (
                                <span
                                    id={QA_IDS.usernameError}
                                    data-testid={QA_IDS.usernameLoginError}
                                    data-qa={QA_IDS.usernameLoginError}
                                    style={styles.errorText}
                                    role="alert"
                                >
                                    {usernameError}
                                </span>
                            )}
                        </div>

                        {/* Password */}
                        <div data-testid={QA_IDS.passwordWrapper} data-qa={QA_IDS.passwordWrapper} style={styles.inputWrapper}>
                            <label
                                id={QA_IDS.passwordLabel}
                                data-testid={QA_IDS.passwordLabel}
                                data-qa={QA_IDS.passwordLabel}
                                htmlFor={QA_IDS.passwordInput}
                                style={styles.label}
                            >
                                Password
                            </label>

                            <div
                                data-testid={QA_IDS.passwordInputContainer}
                                data-qa={QA_IDS.passwordInputContainer}
                                style={styles.inputContainer}
                            >
                                <svg
                                    data-testid={QA_IDS.passwordIcon}
                                    data-qa={QA_IDS.passwordIcon}
                                    style={styles.inputIcon}
                                    width="18"
                                    height="18"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    aria-hidden="true"
                                >
                                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                                    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                                </svg>

                                <input
                                    id={QA_IDS.passwordInput}
                                    data-testid={QA_IDS.passwordInput}
                                    data-qa={QA_IDS.passwordInput}
                                    style={{
                                        ...styles.input,
                                        ...(passwordError ? styles.inputError : {}),
                                    }}
                                    type={showPassword ? "text" : "password"}
                                    placeholder="Enter your password"
                                    value={password}
                                    aria-invalid={Boolean(passwordError)}
                                    aria-describedby={passwordError ? QA_IDS.passwordError : undefined}
                                    autoComplete="current-password"
                                    onChange={(e) => {
                                        setPassword(e.target.value);
                                        validatePassword(e.target.value);
                                        if (loginError) setLoginError("");
                                        if (deactivatedError) setDeactivatedError("");
                                    }}
                                />

                                <button
                                    id={QA_IDS.passwordToggle}
                                    data-testid={QA_IDS.passwordToggle}
                                    data-qa={QA_IDS.passwordToggle}
                                    type="button"
                                    style={styles.eyeButton}
                                    aria-label={showPassword ? "Hide password" : "Show password"}
                                    aria-pressed={showPassword}
                                    onClick={() => setShowPassword(!showPassword)}
                                >
                                    {showPassword ? (
                                        <svg
                                            data-testid={QA_IDS.passwordToggleHideIcon}
                                            data-qa={QA_IDS.passwordToggleHideIcon}
                                            width="18"
                                            height="18"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            aria-hidden="true"
                                        >
                                            <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                                            <line x1="1" y1="1" x2="23" y2="23" />
                                        </svg>
                                    ) : (
                                        <svg
                                            data-testid={QA_IDS.passwordToggleShowIcon}
                                            data-qa={QA_IDS.passwordToggleShowIcon}
                                            width="18"
                                            height="18"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            aria-hidden="true"
                                        >
                                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                            <circle cx="12" cy="12" r="3" />
                                        </svg>
                                    )}
                                </button>
                            </div>

                            {passwordError && (
                                <span
                                    id={QA_IDS.passwordError}
                                    data-testid={QA_IDS.passwordError}
                                    data-qa={QA_IDS.passwordError}
                                    style={styles.errorText}
                                    role="alert"
                                >
                                    {passwordError}
                                </span>
                            )}

                        </div>

                        {/* Form-level login errors */}
                        {loginError && (
                            <div
                                id={QA_IDS.loginError}
                                data-testid={QA_IDS.loginError}
                                data-qa={QA_IDS.loginError}
                                style={styles.loginErrorText}
                                role="alert"
                                aria-live="polite"
                            >
                                {loginError}
                            </div>
                        )}

                        {deactivatedError && (
                            <div
                                id={QA_IDS.deactivatedError}
                                data-testid={QA_IDS.deactivatedError}
                                data-qa={QA_IDS.deactivatedError}
                                style={styles.loginWarningText}
                                role="alert"
                                aria-live="polite"
                            >
                                ⚠ {deactivatedError}. Please contact your administrator.
                            </div>
                        )}

                        <button
                            id={QA_IDS.submitButton}
                            data-testid={QA_IDS.submitButton}
                            data-qa={QA_IDS.submitButton}
                            style={{ ...styles.button, ...(isLoading ? styles.buttonDisabled : {}) }}
                            type="submit"
                            disabled={isLoading}
                            aria-busy={isLoading}
                        >
                            {isLoading ? (
                                <div
                                    data-testid={QA_IDS.submitLoader}
                                    data-qa={QA_IDS.submitLoader}
                                    style={styles.loader}
                                    role="status"
                                    aria-label="Login loading"
                                ></div>
                            ) : (
                                <>
                                    <span data-testid={QA_IDS.submitButtonText} data-qa={QA_IDS.submitButtonText}>
                                        Sign In
                                    </span>
                                    <svg
                                        data-testid={QA_IDS.submitButtonIcon}
                                        data-qa={QA_IDS.submitButtonIcon}
                                        style={styles.buttonIcon}
                                        width="18"
                                        height="18"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        aria-hidden="true"
                                    >
                                        <line x1="5" y1="12" x2="19" y2="12" />
                                        <polyline points="12 5 19 12 12 19" />
                                    </svg>
                                </>
                            )}
                        </button>
                    </form>
                </div>
            </section>
        </div>
    );
};

const styles: { [key: string]: React.CSSProperties } = {
    fullContainer: { minHeight: "100vh", display: "flex", flexDirection: "row", backgroundColor: "#0f172a", position: "relative", overflow: "hidden" },
    bgGradient1: { position: "absolute", width: "600px", height: "600px", background: "radial-gradient(circle, rgba(59,130,246,.15) 0%, transparent 70%)", top: "-200px", left: "-200px", animation: "float 8s ease-in-out infinite", zIndex: 0 },
    bgGradient2: { position: "absolute", width: "500px", height: "500px", background: "radial-gradient(circle, rgba(168,85,247,.12) 0%, transparent 70%)", bottom: "-150px", right: "-150px", animation: "float 10s ease-in-out infinite", animationDelay: "2s", zIndex: 0 },
    bgGradient3: { position: "absolute", width: "400px", height: "400px", background: "radial-gradient(circle, rgba(16,185,129,.1) 0%, transparent 70%)", top: "50%", left: "50%", transform: "translate(-50%,-50%)", animation: "float 12s ease-in-out infinite", animationDelay: "4s", zIndex: 0 },
    bgPattern: { position: "absolute", top: 0, left: 0, right: 0, bottom: 0, backgroundImage: "radial-gradient(circle at 1px 1px, rgba(148,163,184,.05) 1px, transparent 0)", backgroundSize: "40px 40px", zIndex: 0 },
    decorCircle1: { position: "absolute", width: "300px", height: "300px", borderRadius: "50%", border: "1px solid rgba(148,163,184,.1)", top: "10%", left: "5%", animation: "pulse 4s ease-in-out infinite", zIndex: 0 },
    decorCircle2: { position: "absolute", width: "200px", height: "200px", borderRadius: "50%", border: "1px solid rgba(148,163,184,.1)", bottom: "15%", right: "8%", animation: "pulse 5s ease-in-out infinite", animationDelay: "1s", zIndex: 0 },
    leftSide: { flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: "60px", position: "relative", zIndex: 1 },
    brandingContent: { maxWidth: "500px" },
    logoContainer: { display: "flex", alignItems: "center", gap: "16px", marginBottom: "32px" },
    logoIcon: { width: "64px", height: "64px", background: "linear-gradient(135deg,#3b82f6 0%,#8b5cf6 100%)", borderRadius: "16px", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 8px 32px rgba(59,130,246,.3)" },
    brandName: { fontSize: "36px", fontWeight: "800", color: "#ffffff", margin: 0, letterSpacing: "2px" },
    brandTitle: { fontSize: "42px", fontWeight: "800", color: "#ffffff", marginBottom: "20px", lineHeight: "1.2" },
    brandSubtitle: { fontSize: "17px", color: "#94a3b8", lineHeight: "1.7", marginBottom: "40px" },
    features: { display: "flex", flexDirection: "column", gap: "16px" },
    feature: { display: "flex", alignItems: "center", gap: "12px", color: "#e2e8f0", fontSize: "16px" },
    featureIcon: { width: "28px", height: "28px", background: "rgba(16,185,129,.2)", borderRadius: "8px", display: "flex", alignItems: "center", justifyContent: "center", color: "#10b981", fontWeight: "bold" },
    rightSide: { flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: "40px", position: "relative", zIndex: 1 },
    loginCard: { background: "rgba(255,255,255,.98)", backdropFilter: "blur(20px)", borderRadius: "24px", padding: "32px 48px", width: "100%", maxWidth: "480px", boxShadow: "0 25px 60px -15px rgba(0,0,0,.5), 0 0 0 1px rgba(255,255,255,.1)", animation: "slideUp .8s ease-out" },
    header: { marginBottom: "20px" },
    title: { fontSize: "32px", fontWeight: "800", background: "linear-gradient(135deg,#1e3a8a 0%,#3b82f6 50%,#8b5cf6 100%)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text", marginBottom: "8px" },
    subtitle: { fontSize: "15px", color: "#64748b", fontWeight: "500" },
    formContainer: { display: "flex", flexDirection: "column", gap: "18px" },
    inputWrapper: { display: "flex", flexDirection: "column", gap: "6px" },
    label: { fontSize: "14px", fontWeight: "600", color: "#1e293b", marginLeft: "4px" },
    inputContainer: { position: "relative", display: "flex", alignItems: "center" },
    inputIcon: { position: "absolute", left: "16px", color: "#94a3b8", pointerEvents: "none", zIndex: 1 },
    input: { width: "100%", padding: "14px 16px 14px 48px", fontSize: "15px", border: "2px solid #e2e8f0", borderRadius: "12px", outline: "none", backgroundColor: "#ffffff", color: "#1e293b", transition: "all 0.3s ease", fontWeight: "500" },
    inputError: { border: "2px solid #ef4444", backgroundColor: "#fef2f2" },
    errorText: { fontSize: "13px", color: "#ef4444", marginLeft: "4px", fontWeight: "500" },
    loginErrorText: { fontSize: "13px", color: "#b91c1c", backgroundColor: "#fef2f2", border: "1px solid #fecaca", borderRadius: "10px", padding: "10px 12px", fontWeight: "600", textAlign: "center" },
    loginWarningText: { fontSize: "13px", color: "#b45309", backgroundColor: "#fffbeb", border: "1px solid #fde68a", borderRadius: "10px", padding: "10px 12px", fontWeight: "600", textAlign: "center" },
    eyeButton: { position: "absolute", right: "16px", background: "none", border: "none", cursor: "pointer", color: "#94a3b8", padding: "4px", display: "flex", alignItems: "center", justifyContent: "center", transition: "color 0.3s ease" },
    button: { width: "100%", padding: "14px", fontSize: "16px", fontWeight: "700", color: "#ffffff", background: "linear-gradient(135deg,#1e3a8a 0%,#3b82f6 50%,#8b5cf6 100%)", border: "none", borderRadius: "12px", cursor: "pointer", marginTop: "8px", display: "flex", alignItems: "center", justifyContent: "center", gap: "10px", boxShadow: "0 4px 16px rgba(59,130,246,.4)", transition: "all 0.3s ease" },
    buttonDisabled: { opacity: 0.7, cursor: "not-allowed" },
    buttonIcon: { transition: "transform 0.3s ease" },
    loader: { width: "20px", height: "20px", border: "3px solid rgba(255,255,255,.3)", borderTop: "3px solid #ffffff", borderRadius: "50%", animation: "spin 0.8s linear infinite" },
};

export default Login;